import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Table,
  Tag,
  Space,
  Typography,
  Modal,
  Progress,
  Spin
} from "antd";

const { Title, Text } = Typography;

function UploadHealthPolicy() {
  const [files, setFiles] = useState([]);
  const [extractedData, setExtractedData] = useState([]);
  const fileInputRef = useRef(null);

  // Processing modal state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingPercent, setProcessingPercent] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [estimatedSeconds, setEstimatedSeconds] = useState(25);
  const [processingMessage, setProcessingMessage] = useState("");

  const activeProcessesRef = useRef(0);
  const processingStartRef = useRef(0);

  useEffect(() => {
    let timer;

    if (isProcessing) {
      timer = setInterval(() => {
        const elapsed = Math.max(
          0,
          (Date.now() - processingStartRef.current) / 1000
        );

        setElapsedSeconds(Math.floor(elapsed));

        let smoothPercent;

        if (elapsed <= estimatedSeconds) {
          // Estimated time तक progress smoothly 0 से 90% तक जाए
          smoothPercent = (elapsed / estimatedSeconds) * 90;
        } else {
          // Estimate पार होने पर 90 से धीरे-धीरे अधिकतम 97% तक जाए
          const extraRatio = (elapsed - estimatedSeconds) / estimatedSeconds;
          smoothPercent = 90 + (1 - Math.exp(-extraRatio)) * 7;
        }

        setProcessingPercent((prev) =>
          Math.max(prev, Math.min(97, Math.round(smoothPercent)))
        );
      }, 250);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [isProcessing, estimatedSeconds]);

  const formatElapsedTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    return `${String(minutes).padStart(2, "0")}:${String(
      remainingSeconds
    ).padStart(2, "0")}`;
  };

  const estimatedRemainingSeconds = Math.max(
    0,
    estimatedSeconds - elapsedSeconds
  );

  const estimateExceeded = elapsedSeconds > estimatedSeconds;

  const updateProgress = (message) => {
    // Percentage timer से smoothly चलेगी; यहाँ केवल status message बदलेगा।
    setProcessingMessage(message);
  };

  const finishOneProcess = () => {
    activeProcessesRef.current -= 1;

    if (activeProcessesRef.current <= 0) {
      setProcessingPercent(100);
      setProcessingMessage("Processing completed");

      setTimeout(() => {
        setIsProcessing(false);
        setProcessingPercent(0);
        setElapsedSeconds(0);
        setProcessingMessage("");
        activeProcessesRef.current = 0;
        processingStartRef.current = 0;
      }, 500);
    }
  };

  // EXTRACT DATA (filename + type both sent)
  const extractFileData = async (storedFilename, detectedType, key) => {
    try {
      setProcessingMessage("Extracting policy data...");

      const response = await fetch("https://kgonline.in/api/read_pdf.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          filename: storedFilename,
          type: detectedType
        })
      });

      const result = await response.json();

      if (result.error) {
        console.log("Extraction error:", result.error);
        return;
      }

      const structuredData = {
        key: key,
        file_name: storedFilename,
        company: result.company || "",
        policy_number: result.policy_number || "",
        insured_name: result.insured_name || "",
        premium: result.premium || "",
        vehicle_number: result.vehicle_number || "",
        policy_type: detectedType
      };

      setExtractedData((prev) => [...prev, structuredData]);

    } catch (error) {
      console.log("Extraction Error:", error);

    } finally {
      updateProgress("Policy data extracted");
      finishOneProcess();
    }
  };

  // VERIFY FILE
  const verifyFile = async (file, key) => {
    const formData = new FormData();
    formData.append("pdf", file);

    try {
      setProcessingMessage(`Verifying ${file.name}...`);

      const response = await fetch("https://kgonline.in/api/verify_pdf.php", {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      const detectedType = result.detector_response?.type || "Other";
      const storedFilename = result.detector_response?.stored_filename;

      setFiles((prev) =>
        prev.map((f) =>
          f.key === key
            ? {
                ...f,
                type: detectedType,
                status: "Pending",
                stored_filename: storedFilename
              }
            : f
        )
      );

      updateProgress(`${file.name} verified`);

      // pass filename + type
      if (storedFilename) {
        await extractFileData(storedFilename, detectedType, key);
      } else {
        finishOneProcess();
      }

    } catch (error) {
      setFiles((prev) =>
        prev.map((f) =>
          f.key === key
            ? { ...f, type: "Other", status: "Error" }
            : f
        )
      );

      updateProgress(`${file.name} verification failed`);
      finishOneProcess();
    }
  };

  // FILE SELECT
  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);

    if (selectedFiles.length === 0) {
      return;
    }

    activeProcessesRef.current = selectedFiles.length;

    // पिछले OCR समय को आधार मानकर अनुमान:
    // पहली file लगभग 25 सेकंड, हर अतिरिक्त concurrent file के लिए 8 सेकंड।
    const estimatedTotal = 25 + Math.max(0, selectedFiles.length - 1) * 8;

    processingStartRef.current = Date.now();
    setEstimatedSeconds(estimatedTotal);
    setElapsedSeconds(0);
    setProcessingPercent(1);
    setProcessingMessage("Starting processing...");
    setIsProcessing(true);

    selectedFiles.forEach((file) => {
      const uniqueKey = file.name + Date.now();

      const newFile = {
        key: uniqueKey,
        fileObject: file,
        name: file.name,
        type: "Checking...",
        status: "Verifying",
      };

      setFiles((prev) => [...prev, newFile]);
      verifyFile(file, uniqueKey);
    });

    event.target.value = null;
  };

  const handleClearAll = () => {
    setFiles([]);
    setExtractedData([]);
  };

  const columns = [
    { title: "File Name", dataIndex: "name", key: "name" },
    { title: "File Type", dataIndex: "type", key: "type" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color = "default";
        if (status === "Pending") color = "orange";
        if (status === "Verifying") color = "blue";
        if (status === "Error") color = "red";
        return <Tag color={color}>{status}</Tag>;
      },
    },
  ];

  const extractedColumns = [
    //{ title: "File Name", dataIndex: "file_name", key: "file_name" },
    { title: "Company", dataIndex: "company", key: "company" },
    { title: "Policy Number", dataIndex: "policy_number", key: "policy_number" },
    { title: "Insured Name", dataIndex: "insured_name", key: "insured_name" },
    { title: "Premium", dataIndex: "premium", key: "premium" },
    // { title: "Vehicle No", dataIndex: "vehicle_number", key: "vehicle_number" },
    // { title: "Policy Type", dataIndex: "policy_type", key: "policy_type" },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Upload Files</Title>

      <Space style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          onClick={() => fileInputRef.current.click()}
          disabled={isProcessing}
        >
          Select Files
        </Button>

        <Button
          danger
          onClick={handleClearAll}
          disabled={files.length === 0 || isProcessing}
        >
          Clear All
        </Button>
      </Space>

      <input
        type="file"
        multiple
        accept="application/pdf"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileSelect}
        disabled={isProcessing}
      />

      <Table
        columns={columns}
        dataSource={files}
        pagination={false}
        bordered
        style={{ marginBottom: 30 }}
      />

      <Title level={4} style={{ marginTop: 30 }}>
        Health Insurance
      </Title>

      <Table
        columns={extractedColumns}
        dataSource={extractedData}
        pagination={false}
        bordered
        locale={{ emptyText: "No extracted data yet" }}
      />

      <Modal
        open={isProcessing}
        footer={null}
        closable={false}
        maskClosable={false}
        keyboard={false}
        centered
        width={460}
        maskStyle={{
          backgroundColor: "rgba(0, 0, 0, 0.65)",
          cursor: "not-allowed"
        }}
      >
        <div
          style={{
            textAlign: "center",
            padding: "18px 8px 10px"
          }}
        >
          <Spin size="large" />

          <Title level={4} style={{ marginTop: 20, marginBottom: 8 }}>
            Processing Policy Files
          </Title>

          <Text type="secondary">
            {processingMessage || "Please wait..."}
          </Text>

          <Progress
            percent={processingPercent}
            status={processingPercent === 100 ? "success" : "active"}
            strokeLinecap="round"
            style={{ marginTop: 24 }}
          />

          <div
            style={{
              marginTop: 16,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              textAlign: "left"
            }}
          >
            <div>
              <Text type="secondary">Elapsed time</Text>
              <div>
                <Text strong>{formatElapsedTime(elapsedSeconds)}</Text>
              </div>
            </div>

            <div>
              <Text type="secondary">Estimated total</Text>
              <div>
                <Text strong>{formatElapsedTime(estimatedSeconds)}</Text>
              </div>
            </div>

            <div>
              <Text type="secondary">Estimated remaining</Text>
              <div>
                <Text strong>
                  {estimateExceeded
                    ? "Finishing..."
                    : formatElapsedTime(estimatedRemainingSeconds)}
                </Text>
              </div>
            </div>

            <div>
              <Text type="secondary">Current status</Text>
              <div>
                <Text strong>
                  {processingPercent < 90
                    ? "Processing"
                    : processingPercent < 100
                    ? "Finalizing"
                    : "Completed"}
                </Text>
              </div>
            </div>
          </div>

          {estimateExceeded && processingPercent < 100 && (
            <div style={{ marginTop: 10 }}>
              <Text type="warning">
                Estimated time exceeded; processing is still active.
              </Text>
            </div>
          )}

          <div style={{ marginTop: 10 }}>
            <Text type="secondary">
              Processing के दौरान कृपया कोई action न करें।
            </Text>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default UploadHealthPolicy;
